export enum EProjectPermission {
  // NOTE:Wild card permission represents all permissions (admin)
  WILD_CARD = "*",

  PROJECT_READ = "project:read",
  PROJECT_WRITE = "project:write",
  PROJECT_DELETE = "project:delete",

  ROLE_READ = "role:read",
  ROLE_WRITE = "role:write",
  ROLE_DELETE = "role:delete",

  PROJECT_INVITE = "invite:write",

  PROJECT_MEMBER_DELETE = "project_member:delete",

  ALL_MEMBERS_ANALYTICS_READ = "all_members_analytics:read",

  ANALYTICS_READ = "analytics:read",

  PROMPT_LIBRARY_READ = "prompt_library:read",
  PROMPT_LIBRARY_WRITE = "prompt_library:write",
  PROMPT_LIBRARY_DELETE = "prompt_library:delete",

  PROBE_READ = "probe:read",
  PROBE_WRITE = "probe:write",
  PROBE_DELETE = "probe:delete",

  PRIVATE_ASSISTANT_READ = "private_assistant:read",
  PRIVATE_ASSISTANT_WRITE = "private_assistant:write",
  PRIVATE_ASSISTANT_DELETE = "private_assistant:delete",

  PRIVATE_ASSISTANT_SHARE_WRITE = "private_assistant_share:write",

  PUBLIC_ASSISTANT_READ = "public_assistant:read",
  PUBLIC_ASSISTANT_WRITE = "public_assistant:write",
  PUBLIC_ASSISTANT_DELETE = "public_assistant:delete",

  // NOTE: NSAI-3830 — organization-wide assistant marketplace. READ browses and
  // uses published/default marketplace assistants; WRITE publishes/unpublishes
  // and manages listing visibility.
  MARKETPLACE_ASSISTANT_READ = "marketplace_assistant:read",
  MARKETPLACE_ASSISTANT_WRITE = "marketplace_assistant:write",

  // NOTE: NSAI-5272 — organization-wide automation marketplace (scheduled tasks
  // and webhooks). READ browses published listings and installs a copy into the
  // current project; WRITE publishes/unpublishes and manages listing
  // visibility. Independent of the assistant marketplace permissions.
  MARKETPLACE_AUTOMATION_READ = "marketplace_automation:read",
  MARKETPLACE_AUTOMATION_WRITE = "marketplace_automation:write",

  // NOTE: Agent Skills use per-visibility permissions, mirroring the assistant
  // model. There is deliberately no generic `skill:*` permission: a role must be
  // able to manage its own private skills without gaining access to the
  // project-wide public ones.
  PRIVATE_SKILL_READ = "private_skill:read",
  PRIVATE_SKILL_WRITE = "private_skill:write",
  PRIVATE_SKILL_DELETE = "private_skill:delete",

  PUBLIC_SKILL_READ = "public_skill:read",
  PUBLIC_SKILL_WRITE = "public_skill:write",
  PUBLIC_SKILL_DELETE = "public_skill:delete",

  // NOTE: Skill sharing (NSAI-3309) and the organization catalog (NSAI-3311)
  // are not wired up yet; the values exist so both tickets build on
  // already-granted permissions. `organization_skill:*` deliberately breaks the
  // per-visibility read/write/delete shape — the catalog is org-scoped, so it
  // only distinguishes reading it from curating it.
  PRIVATE_SKILL_SHARE_WRITE = "private_skill_share:write",
  ORGANIZATION_SKILL_READ = "organization_skill:read",
  ORGANIZATION_SKILL_CURATE = "organization_skill:curate",

  INTEGRATION_READ = "integration:read",
  INTEGRATION_WRITE = "integration:write",
  INTEGRATION_DELETE = "integration:delete",

  MODEL_READ = "model:read",
  MODEL_WRITE = "model:write",
  MODEL_DELETE = "model:delete",

  MODEL_VISIBILITY_WRITE = "model_visibility:write",

  CHAT_SETTINGS_READ = "chat_settings:read",
  CHAT_SETTINGS_WRITE = "chat_settings:write",

  API_KEY_READ = "api_key:read",
  API_KEY_WRITE = "api_key:write",
  API_KEY_DELETE = "api_key:delete",

  KNOWLEDGE_READ = "knowledge:read",
  KNOWLEDGE_WRITE = "knowledge:write",
  KNOWLEDGE_DELETE = "knowledge:delete",

  PUBLIC_KNOWLEDGE_WRITE = "public_knowledge:write",

  AUTOMATION_READ = "automation:read",
  AUTOMATION_WRITE = "automation:write",
  AUTOMATION_DELETE = "automation:delete",

  AUTOMATION_MANAGER_READ = "automation_manager:read",
  AUTOMATION_MANAGER_WRITE = "automation_manager:write",
  AUTOMATION_MANAGER_DELETE = "automation_manager:delete",

  CUSTOM_INTEGRATION_READ = "custom_integration:read",
  CUSTOM_INTEGRATION_WRITE = "custom_integration:write",
  CUSTOM_INTEGRATION_DELETE = "custom_integration:delete",

  CHAT_SHARE_READ = "chat_share:read",
  CHAT_SHARE_WRITE = "chat_share:write",

  CALL_ASSISTANT_READ = "call_assistant:read",
  CALL_ASSISTANT_WRITE = "call_assistant:write",
  CALL_ASSISTANT_DELETE = "call_assistant:delete",

  // NOTE: NSAI-4317 — the document recognition review queue. READ sees the queue
  // and opens a document, WRITE claims it and corrects fields, DELETE rejects a
  // document and can force-release another reviewer's lock. Configuring the
  // watched folder is deliberately NOT here: it is organization-owner only, which
  // a project permission cannot express.
  DOC_RECOGNITION_READ = "doc_recognition:read",
  DOC_RECOGNITION_WRITE = "doc_recognition:write",
  DOC_RECOGNITION_DELETE = "doc_recognition:delete",

  ORGANIZATION_SELECTOR_READ = "organization_selector:read",

  // NOTE: NSAI-3376 — read-only visibility of the organization's token usage
  // limits for non-owner members. Configuring limits stays owner-only, so there
  // is intentionally no matching write scope.
  USAGE_LIMITS_READ = "usage_limits:read",

  // NOTE: NSAI-6213 — tenant-side SIEM export configuration, previously
  // organization-owner only. READ shows the SIEM Export tab and the stored
  // configuration, WRITE edits it and runs the connection test, DELETE turns
  // export off or clears a configured destination. Owners always have all
  // three; the server is the authority, these values only drive what the UI
  // offers.
  SIEM_CONFIG_READ = "siem_config:read",
  SIEM_CONFIG_WRITE = "siem_config:write",
  SIEM_CONFIG_DELETE = "siem_config:delete",

  // NOTE: NSAI-6369 — the organization-wide list of categories memory must
  // never store about any member. READ shows the Memory policy section and the
  // stored list, WRITE edits it. There is no DELETE level: clearing the policy
  // is writing an empty list. Owners always have both; the server is the
  // authority, these values only drive what the UI offers.
  MEMORY_POLICY_READ = "memory_policy:read",
  MEMORY_POLICY_WRITE = "memory_policy:write",

  // NOTE: NSAI-5657 — saved dashboards. READ opens the Dashboards section, the
  // caller's own dashboards and the ones shared with them, and refreshes them
  // (a refresh runs with the caller's own access). WRITE saves a page as a
  // dashboard, edits, versions, duplicates and shares one; DELETE removes one
  // the caller owns. The server is the authority; these values only drive what
  // the UI offers.
  DASHBOARD_READ = "dashboard:read",
  DASHBOARD_WRITE = "dashboard:write",
  DASHBOARD_DELETE = "dashboard:delete",

  // NOTE: NSAI-5657 — pin a dashboard for everyone in the project, which also
  // opens it to them. Write only: there is nothing to read or delete about a
  // pin beyond what the dashboard family already covers.
  DASHBOARD_MANAGER_WRITE = "dashboard_manager:write",
}

export type TPermissionsMap = Record<string, string[]>;
